import { PageTitle } from "components/PageTitle";
import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import { Table } from "components/ui/Table/Table";
import {
  type AdminPlan,
  type AdminPlanLimit,
  type UpsertPlanLimitInput,
  useAdminPlansApi,
  useCreateAdminPlanApi,
  useUpdateAdminPlanApi,
  useUpsertAdminPlanLimitApi,
} from "hooks/useAdminApi";
import { type FC, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toastPromise } from "utils/toast-utils";

type EditableLimit = AdminPlanLimit;

type EditModalState = {
  plan: AdminPlan;
  displayName: string;
  limits: EditableLimit[];
};

type CreateModalState = {
  name: string;
  displayName: string;
};

/** 利用カテゴリの表示順と日本語ラベル（バックエンドと同じ並び） */
const CATEGORY_ORDER: { category: string; label: string }[] = [
  { category: "email", label: "メール受信" },
  { category: "matching", label: "マッチング" },
  { category: "delivery", label: "一括配信" },
  { category: "ai_helper", label: "AIヘルパー" },
  { category: "ai_compose", label: "AI自動メール生成" },
];

/**
 * 既存の limits 配列から category の上限を取得し、無ければ 0 で初期化する
 * @param {AdminPlanLimit[]} limits プランの上限一覧
 * @param {string} category 利用カテゴリ
 * @param {string} label 表示ラベル
 * @return {AdminPlanLimit} 既存または初期値の上限
 */
const pickOrInitLimit = (
  limits: AdminPlanLimit[],
  category: string,
  label: string,
): AdminPlanLimit => {
  const existing = limits.find(l => l.category === category);
  if (existing) return existing;
  return {
    category,
    label,
    monthlyLimit: 0,
    overageLimit: 0,
    overageUnitPrice: "0",
    warningThresholdPct: 80,
  };
};

/**
 * プラン管理ページ（SYSTEM_ADMIN専用）
 * プラン一覧を表示し、モーダルで表示名とカテゴリ別上限を編集する
 * @return {JSX.Element} プラン管理ページ
 */
export const PlansPage: FC = () => {
  const { data, loading, refetch } = useAdminPlansApi();
  const { updatePlan, loading: isUpdatingPlan } = useUpdateAdminPlanApi();
  const { createPlan, loading: isCreatingPlan } = useCreateAdminPlanApi();
  const { upsertLimit, loading: isUpsertingLimit } =
    useUpsertAdminPlanLimitApi();

  const [editing, setEditing] = useState<EditModalState | null>(null);
  const [creating, setCreating] = useState<CreateModalState | null>(null);
  const [formError, setFormError] = useState("");
  const [createError, setCreateError] = useState("");

  const isSaving = isUpdatingPlan || isUpsertingLimit;

  const handleOpenEdit = (plan: AdminPlan) => {
    const limits = CATEGORY_ORDER.map(({ category, label }) =>
      pickOrInitLimit(plan.limits, category, label),
    );
    setEditing({
      plan,
      displayName: plan.displayName,
      limits,
    });
    setFormError("");
  };

  const handleClose = () => {
    if (isSaving) return;
    setEditing(null);
  };

  const handleLimitChange = (
    category: string,
    field: keyof UpsertPlanLimitInput,
    value: string,
  ) => {
    setEditing(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        limits: prev.limits.map(l =>
          l.category === category
            ? {
                ...l,
                [field]:
                  field === "overageUnitPrice" ? value : Number(value || 0),
              }
            : l,
        ),
      };
    });
  };

  const validate = (state: EditModalState): string | null => {
    if (!state.displayName.trim()) return "表示名を入力してください";
    for (const limit of state.limits) {
      if (limit.monthlyLimit < 0 || limit.overageLimit < 0) {
        return `${limit.label}: 上限値は 0 以上にしてください`;
      }
      if (limit.warningThresholdPct < 0 || limit.warningThresholdPct > 100) {
        return `${limit.label}: 警告閾値は 0〜100 の範囲で入力してください`;
      }
      if (Number(limit.overageUnitPrice) < 0) {
        return `${limit.label}: 超過単価は 0 以上にしてください`;
      }
    }
    return null;
  };

  const handleSave = async () => {
    if (!editing) return;
    const err = validate(editing);
    if (err) {
      setFormError(err);
      return;
    }
    setFormError("");
    try {
      await toastPromise(
        (async () => {
          if (editing.displayName !== editing.plan.displayName) {
            await updatePlan(editing.plan.id, editing.displayName);
          }
          for (const limit of editing.limits) {
            await upsertLimit(editing.plan.id, limit.category, {
              monthlyLimit: limit.monthlyLimit,
              overageLimit: limit.overageLimit,
              overageUnitPrice: limit.overageUnitPrice,
              warningThresholdPct: limit.warningThresholdPct,
            });
          }
        })(),
        {
          success: "プランを更新しました",
          error: "プランの更新に失敗しました",
        },
      );
      setEditing(null);
      await refetch();
    } catch (_e) {
      // toastPromise がエラー表示済み
    }
  };

  const handleOpenCreate = () => {
    setCreating({ name: "", displayName: "" });
    setCreateError("");
  };

  const handleCreate = async () => {
    if (!creating) return;
    const name = creating.name.trim();
    const displayName = creating.displayName.trim();
    if (!name || !displayName) {
      setCreateError("識別子と表示名の両方を入力してください");
      return;
    }
    if (!/^[a-z][a-z0-9_-]*$/.test(name)) {
      setCreateError(
        "識別子は小文字英字で始まり、英数字・ハイフン・アンダースコアのみ使用できます",
      );
      return;
    }
    setCreateError("");
    const creation = createPlan({ name, displayName });
    try {
      await toastPromise(creation, {
        success: "プランを作成しました",
        error: "プランの作成に失敗しました",
      });
      const created = await creation;
      setCreating(null);
      await refetch();
      handleOpenEdit(created);
    } catch (_e) {
      // toastPromise がエラー表示済み
    }
  };

  const plans = useMemo(() => data?.items ?? [], [data]);

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <PageTitle>プラン管理</PageTitle>
        <div className="flex items-center gap-2">
          <Button type="button" size="sm" onClick={handleOpenCreate}>
            + 新規プラン作成
          </Button>
          <Button asChild variant="ghost" size="sm">
            <Link to="/admin">← 管理画面に戻る</Link>
          </Button>
        </div>
      </div>

      <div className="card bg-base-100 shadow-md">
        <div className="card-body">
          <h2 className="card-title text-color-title">
            サブスクリプションプラン
          </h2>
          <p className="text-sm text-gray-500 mb-2">
            プランの表示名とカテゴリ別上限を編集できます。変更は既存テナントに即座に反映されます。
          </p>

          {loading ? (
            <div className="flex justify-center py-4">
              <Loading size="md" />
            </div>
          ) : plans.length === 0 ? (
            <p className="text-gray-500 text-center py-4">
              プランが登録されていません
            </p>
          ) : (
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
              <Table.Root density="compact" zebra stickyHeader>
                <Table.Head sticky>
                  <tr>
                    <Table.HeaderCell>識別子</Table.HeaderCell>
                    <Table.HeaderCell>表示名</Table.HeaderCell>
                    <Table.HeaderCell>上限設定カテゴリ数</Table.HeaderCell>
                    <Table.HeaderCell>更新日時</Table.HeaderCell>
                    <Table.HeaderCell> </Table.HeaderCell>
                  </tr>
                </Table.Head>
                <Table.Body>
                  {plans.map(plan => (
                    <Table.Row key={plan.id}>
                      <Table.Cell>
                        <span className="font-mono text-xs">{plan.name}</span>
                      </Table.Cell>
                      <Table.Cell>{plan.displayName}</Table.Cell>
                      <Table.Cell>{plan.limits.length}</Table.Cell>
                      <Table.Cell>
                        {new Date(plan.updatedAt).toLocaleString("ja-JP")}
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          type="button"
                          size="xs"
                          onClick={() => handleOpenEdit(plan)}
                        >
                          編集
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <div className="modal modal-open">
          <div className="modal-box max-w-3xl">
            <h3 className="font-bold text-lg text-color-title">
              プラン編集 - {editing.plan.name}
            </h3>
            {formError && (
              <div className="alert alert-error mt-2">
                <span>{formError}</span>
              </div>
            )}
            <div className="space-y-4 py-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">表示名</span>
                </label>
                <input
                  type="text"
                  className="input input-bordered input-sm w-full"
                  value={editing.displayName}
                  onChange={e =>
                    setEditing(prev =>
                      prev ? { ...prev, displayName: e.target.value } : prev,
                    )
                  }
                />
              </div>

              <div>
                <h4 className="font-semibold text-color-title mb-2">
                  カテゴリ別上限
                </h4>
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <Table.Root density="compact">
                    <Table.Head>
                      <tr>
                        <Table.HeaderCell>カテゴリ</Table.HeaderCell>
                        <Table.HeaderCell>月間上限</Table.HeaderCell>
                        <Table.HeaderCell>超過許容量</Table.HeaderCell>
                        <Table.HeaderCell>超過単価(円)</Table.HeaderCell>
                        <Table.HeaderCell>警告閾値(%)</Table.HeaderCell>
                      </tr>
                    </Table.Head>
                    <Table.Body>
                      {editing.limits.map(limit => (
                        <Table.Row key={limit.category}>
                          <Table.Cell>{limit.label}</Table.Cell>
                          <Table.Cell>
                            <input
                              type="number"
                              min={0}
                              className="input input-bordered input-xs w-24"
                              value={limit.monthlyLimit}
                              onChange={e =>
                                handleLimitChange(
                                  limit.category,
                                  "monthlyLimit",
                                  e.target.value,
                                )
                              }
                            />
                          </Table.Cell>
                          <Table.Cell>
                            <input
                              type="number"
                              min={0}
                              className="input input-bordered input-xs w-24"
                              value={limit.overageLimit}
                              onChange={e =>
                                handleLimitChange(
                                  limit.category,
                                  "overageLimit",
                                  e.target.value,
                                )
                              }
                            />
                          </Table.Cell>
                          <Table.Cell>
                            <input
                              type="number"
                              min={0}
                              step="0.01"
                              className="input input-bordered input-xs w-24"
                              value={limit.overageUnitPrice}
                              onChange={e =>
                                handleLimitChange(
                                  limit.category,
                                  "overageUnitPrice",
                                  e.target.value,
                                )
                              }
                            />
                          </Table.Cell>
                          <Table.Cell>
                            <input
                              type="number"
                              min={0}
                              max={100}
                              className="input input-bordered input-xs w-20"
                              value={limit.warningThresholdPct}
                              onChange={e =>
                                handleLimitChange(
                                  limit.category,
                                  "warningThresholdPct",
                                  e.target.value,
                                )
                              }
                            />
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                </div>
              </div>
            </div>
            <div className="modal-action">
              <Button
                type="button"
                variant="ghost"
                onClick={handleClose}
                disabled={isSaving}
              >
                閉じる
              </Button>
              <Button type="button" onClick={handleSave} disabled={isSaving}>
                {isSaving ? <Loading size="sm" /> : "保存"}
              </Button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={handleClose}></div>
        </div>
      )}

      {creating && (
        <div className="modal modal-open">
          <div className="modal-box max-w-md">
            <h3 className="font-bold text-lg text-color-title">
              新規プラン作成
            </h3>
            {createError && (
              <div className="alert alert-error mt-2">
                <span>{createError}</span>
              </div>
            )}
            <div className="space-y-4 py-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">識別子（英小文字スラッグ）</span>
                </label>
                <input
                  type="text"
                  className="input input-bordered input-sm w-full font-mono"
                  placeholder="professional"
                  value={creating.name}
                  onChange={e =>
                    setCreating(prev =>
                      prev ? { ...prev, name: e.target.value } : prev,
                    )
                  }
                />
                <p className="text-xs text-gray-400 mt-1">
                  作成後に変更不可。例: free / starter / professional
                </p>
              </div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text">表示名</span>
                </label>
                <input
                  type="text"
                  className="input input-bordered input-sm w-full"
                  placeholder="Professional"
                  value={creating.displayName}
                  onChange={e =>
                    setCreating(prev =>
                      prev ? { ...prev, displayName: e.target.value } : prev,
                    )
                  }
                />
              </div>
            </div>
            <div className="modal-action">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setCreating(null)}
                disabled={isCreatingPlan}
              >
                閉じる
              </Button>
              <Button
                type="button"
                onClick={handleCreate}
                disabled={isCreatingPlan}
              >
                {isCreatingPlan ? <Loading size="sm" /> : "作成して編集へ"}
              </Button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => setCreating(null)}
          ></div>
        </div>
      )}
    </div>
  );
};

export default PlansPage;
